import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "src/context/AuthContext";
import { postMethodApiCall } from "src/services/api";

const AuthForm = ({ type }) => {
  const { t } = useTranslation();
  const { login } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [website, setWebsite] = useState("");

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    clearErrors,
  } = useForm({
      mode: "onChange",
      reValidateMode: "onChange",


    // reValidateMode: "onChange",
  });

  // FIX: Clear all errors and inputs when the user switches tabs
  useEffect(() => {
    setLoginError(""); 
    clearErrors();     
    reset({ identifier: "", password: "" }); 
  }, [type, reset, clearErrors]);

  // Configuration for labels and pre-API validation
  const loginConfig = {
    admin: {
      endpoint: "/v1/api/admin/login",
      redirect: "/admin/dashboard",
      label: t("form.labels.Email"),
      placeholder: t("form.placeholders.Email"),
      identityKey: "username",
      validation: {
        required: t("form.errors.required"),
        pattern: {
          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
          message: "Please enter a valid email address",
        },
      },
    },
    mcc: {
      endpoint: "/v1/api/admin/login",
      redirect: "/mcc/mapping",
      label: t("form.labels.Email"),
      placeholder: t("form.placeholders.Email"),
      identityKey: "username",
      validation: {
        required: t("form.errors.required"),
        pattern: {
          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
          message: "Please enter a valid email address",
        },
      },
    },
    trainee: {
      endpoint: "/v1/api/trainee/login",
      redirect: "/Trainee-details",
      label: t("form.labels.username"),
      placeholder: t("form.placeholders.username"),
      identityKey: "username",
      validation: {
        required: t("form.errors.required"),
        pattern: {
          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
          message: "Please enter a valid email address",
        },
      },
    },
    trainer: {
      endpoint: "/v1/api/trainer/login",
      redirect: "/trainer/dashboard",
      label: t("form.labels.username"),
      placeholder: t("form.placeholders.phoneNumber"),
      identityKey: "phone_number", // Trainers use phone_number
      validation: {
        required: t("form.errors.required"),
        pattern: {
          value: /^[0-9]{10}$/,
          message: "Enter a valid 10-digit phone number",
        },
      },
    },
    iti: {
      endpoint: "/v1/api/iti/login",
      redirect: "/iti-home",
      label: t("form.labels.username"),
      placeholder: t("form.placeholders.username"),
      identityKey: "username",
      validation: {
        required: t("form.errors.required"),
        pattern: {
          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
          message: "Please enter a valid email address",
        },
      },
    },
  };

  const current = loginConfig[type];

  const handleLogin = async (data) => {
    setLoading(true);
    setLoginError("");

    try {
      let deviceHeaders = { loginType: type };
      
      // Handle unique Trainee device logic
      if (type === "trainee") {
        const todayKey = new Date().toDateString();
        let deviceId = localStorage.getItem("device_id");
        if (!deviceId || localStorage.getItem("device_id_date") !== todayKey) {
          deviceId = (crypto && crypto.randomUUID) ? crypto.randomUUID() : `${Date.now()}`;
          localStorage.setItem("device_id", deviceId);
          localStorage.setItem("device_id_date", todayKey);
        }
        deviceHeaders["x-device-id"] = deviceId;
      }

      const payload = {
        [current.identityKey]: data.identifier,
        password: data.password,
        website,
        ...(type === "admin" || type === "mcc" ? { portal: type } : {}),
      };

      const res = await postMethodApiCall(current.endpoint, payload, deviceHeaders );

      const portalToken = res?.data?.token;
      const resolvedRole = res?.data?.role || type;
      const isPortalLogin =
        res?.data?.success ||
        ((type === "admin" || type === "mcc") && portalToken);

      if (isPortalLogin) {
        login(portalToken, resolvedRole);
        localStorage.setItem("token", portalToken);
        localStorage.setItem("loginedUser", JSON.stringify(res.data));

        // Dynamic data extraction based on response structure
        const userObj = res.data[type] || res.data.trainee || res.data;
        localStorage.setItem(
          "Name",
          JSON.stringify(userObj?.name || userObj?.iti_name || "User")
        );
        localStorage.setItem(
          "District",
          JSON.stringify(userObj?.district || userObj?.district_of_ITI || "")
        );

        const target =
          resolvedRole === "mcc"
            ? "/mcc/mapping"
            : resolvedRole === "admin"
              ? "/admin/dashboard"
              : current.redirect;
        navigate(target);
      } else {
        // Error handling pattern from details.js
        const errorMsg = res?.data?.error || "Invalid Credentials";
        setLoginError(errorMsg);
        // setTimeout(() => setLoginError(""), 3000); 
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || "Login failed. Please try again.";
      setLoginError(errorMsg);
      // setTimeout(() => setLoginError(""), 3000);
    } finally {
      setLoading(false);
    }
  };

  const clearBackendError = () => {
    if (loginError) {
      setLoginError("");
    }
  };
  // const changeLanguage = (lng) => {
  //   i18n.changeLanguage(lng);
  // };

  return (
    <form onSubmit={handleSubmit(handleLogin)} className="flex flex-col gap-4 w-full">
      <div className="flex flex-col gap-1">
        <label className="font-[400] text-xs text-gray-600">{current.label}</label>
        <input
          {...register("identifier", {...current.validation, onChange: clearBackendError})}
          className={`border rounded-md px-3 py-2 text-sm w-full outline-none transition-all ${
            errors.identifier ? "border-red-500" : "border-gray-300 focus:border-primary"
          }`}
          placeholder={current.placeholder}
          disabled={loading}
        />
        {errors.identifier && (
          <span className="text-red-500 text-xs mt-1 font-medium">{errors.identifier.message}</span>
        )}
      </div>

      <div className="flex flex-col gap-1">
        <label className="font-[400] text-xs text-gray-600">{t("form.labels.Password")}</label>
        <input
          {...register("password", { required: t("form.errors.required"), onChange: clearBackendError })}
          type="password"
          className={`border rounded-md px-3 py-2 text-sm w-full outline-none transition-all ${
            errors.password ? "border-red-500" : "border-gray-300 focus:border-primary"
          }`}
          placeholder={t("form.placeholders.Password")}
          disabled={loading}
        />
        {errors.password && (
          <span className="text-red-500 text-xs mt-1 font-medium">{errors.password.message}</span>
        )}
      </div>

      {/* Backend error display (Details.js style) */}
      {loginError && (
        <span className="text-red-500 text-xs mt-1 font-medium">{loginError}</span>
      )}

      {/* Honeypot field */}
      <input
        type="text"
        value={website}
        onChange={(e) => setWebsite(e.target.value)}
        className="absolute left-[-9999px] opacity-0"
        tabIndex="-1"
        autoComplete="off"
      />

      <button
        type="submit"
        disabled={loading}
        className={`py-2 mt-2 bg-primary text-white w-full rounded-md font-semibold transition shadow-md ${
          loading ? "opacity-60 cursor-not-allowed" : "hover:bg-primary/90 active:scale-[0.98]"
        }`}
      >
        {loading ? t("login.loading") : t("login.title")}
      </button>
          {/* <div className="mt-auto flex justify-center pt-4">
              <span
                className={`cursor-pointer ${
                  i18n.language === "en"
                    ? "text-[#0e766c] font-medium"
                    : "text-gray-500"
                }`}
                onClick={() => changeLanguage("en")}
              >
                English
              </span>
              <span className="mx-2">|</span>
              <span
                className={`cursor-pointer ${
                  i18n.language === "hi"
                    ? "text-[#0e766c] font-medium"
                    : "text-gray-500"
                }`}
                onClick={() => changeLanguage("hi")}
              >
                हिंदी
              </span>
            </div> */}
    
    </form>
  );
};

export default AuthForm;